<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsItemResponse, RequiredCustomerField } from '@/types/carts-transactions-api';

import ContactFieldInput from '@/components/cart/v2/cart-item/ContactFieldInput.vue';
import MobileModal from '@/components/MobileModal.vue';
import { useGuestLogin } from '@/composables/carts/useGuestLogin';

const props = defineProps<{
  cartsTransactionsApi: CartsTransactionsApi;
  requiredCustomerFields: RequiredCustomerField[];
  cartItems: CartDetailsItemResponse[];
}>();

const emit = defineEmits<{
  (e: 'customerCreated'): void;
}>();

const requiredCustomerFieldsRef = computed(() => props.requiredCustomerFields);
const cartItemsRef = computed(() => props.cartItems);

const { t } = useI18n();

const {
  isModalOpen,
  isSubmitting,
  submitError,
  firstname,
  name,
  email,
  confirmEmail,
  phone,
  additionalFieldValues,
  additionalFields,
  emailsMatch,
  emailFormatError,
  phoneFormatError,
  isFormValid,
  phoneRequired,
  openModal,
  closeModal,
  loadExistingCustomer,
  submitGuestCustomer,
} = useGuestLogin(props.cartsTransactionsApi, requiredCustomerFieldsRef, cartItemsRef);

const showAdditionalFields = computed(() => additionalFields.value.length > 0);

function fieldInputId(field: RequiredCustomerField): string {
  return `guest-additional-${field.semanticType}-${field.name}`;
}

function toBookingContactField(field: RequiredCustomerField) {
  return {
    sourceId: null,
    name: field.name,
    type: field.type,
    defaultValue: field.defaultValue,
    required: field.required,
    availableValues: field.availableValues ?? [],
  };
}

async function handleSubmit() {
  await submitGuestCustomer();
  if (!submitError.value) {
    emit('customerCreated');
  }
}

defineExpose({ openModal, closeModal, loadExistingCustomer });
</script>

<template>
  <MobileModal
    :is-open="isModalOpen"
    :title="t('cart.guestLogin.title')"
    @close="closeModal"
  >
    <template #body>
      <div class="guest-form">
        <div class="form-field">
          <label for="guest-firstname" class="field-label">
            {{ t('cart.guestLogin.firstname') }} <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="guest-firstname"
            v-model="firstname"
            type="text"
            class="field-input"
            :placeholder="t('cart.guestLogin.firstnamePlaceholder')"
            autocomplete="given-name"
            aria-required="true"
          >
        </div>

        <div class="form-field">
          <label for="guest-name" class="field-label">
            {{ t('cart.guestLogin.name') }} <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="guest-name"
            v-model="name"
            type="text"
            class="field-input"
            :placeholder="t('cart.guestLogin.namePlaceholder')"
            autocomplete="family-name"
            aria-required="true"
          >
        </div>

        <div class="form-field">
          <label for="guest-email" class="field-label">
            {{ t('cart.guestLogin.email') }} <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="guest-email"
            v-model="email"
            type="email"
            class="field-input"
            :class="{ 'field-error': emailFormatError }"
            placeholder="email@example.com"
            autocomplete="email"
            aria-required="true"
            :aria-invalid="emailFormatError ? 'true' : undefined"
            :aria-describedby="emailFormatError ? 'guest-email-error' : undefined"
          >
          <p v-if="emailFormatError" id="guest-email-error" class="error-message" role="alert">
            {{ t('cart.guestLogin.emailFormatError') }}
          </p>
        </div>

        <div class="form-field">
          <label for="guest-confirm-email" class="field-label">
            {{ t('cart.guestLogin.confirmEmail') }} <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            id="guest-confirm-email"
            v-model="confirmEmail"
            type="email"
            class="field-input"
            :class="{ 'field-error': confirmEmail && !emailsMatch }"
            placeholder="email@example.com"
            autocomplete="email"
            aria-required="true"
            :aria-invalid="confirmEmail && !emailsMatch ? 'true' : undefined"
            :aria-describedby="confirmEmail && !emailsMatch ? 'guest-confirm-email-error' : undefined"
          >
          <p v-if="confirmEmail && !emailsMatch" id="guest-confirm-email-error" class="error-message" role="alert">
            {{ t('cart.guestLogin.emailMismatchError') }}
          </p>
        </div>

        <div class="form-field">
          <label for="guest-phone" class="field-label">
            {{ t('cart.guestLogin.phone') }}
            <span v-if="phoneRequired" class="required" aria-hidden="true">*</span>
            <span v-else class="optional">({{ t('cart.guestLogin.phoneOptional') }})</span>
          </label>
          <input
            id="guest-phone"
            v-model="phone"
            type="tel"
            class="field-input"
            :class="{ 'field-error': phoneFormatError }"
            :placeholder="t('cart.guestLogin.phonePlaceholder')"
            autocomplete="tel"
            :aria-required="phoneRequired ? 'true' : undefined"
            :aria-invalid="phoneFormatError ? 'true' : undefined"
            :aria-describedby="phoneFormatError ? 'guest-phone-error' : undefined"
          >
          <p v-if="phoneFormatError" id="guest-phone-error" class="error-message" role="alert">
            {{ t('cart.guestLogin.phoneFormatError') }}
          </p>
        </div>

        <template v-if="showAdditionalFields">
          <hr class="section-separator" aria-hidden="true">
          <div
            v-for="field in additionalFields"
            :key="field.name"
            class="form-field"
          >
            <label :for="fieldInputId(field)" class="field-label">
              {{ field.name }}
              <span v-if="field.required" class="required" aria-hidden="true">*</span>
              <span v-else class="optional">({{ t('cart.guestLogin.phoneOptional') }})</span>
            </label>
            <ContactFieldInput
              :field="toBookingContactField(field)"
              :input-id="fieldInputId(field)"
              :model-value="additionalFieldValues[field.name] ?? field.defaultValue ?? ''"
              @update:model-value="additionalFieldValues[field.name] = $event"
            />
          </div>
        </template>

        <p v-if="submitError" class="submit-error" role="alert" aria-live="assertive">
          {{ t(submitError) }}
        </p>
      </div>
    </template>

    <template #footer>
      <button
        type="button"
        class="yb-apply-button"
        :disabled="!isFormValid || isSubmitting"
        :aria-busy="isSubmitting"
        @click="handleSubmit"
      >
        <span v-if="isSubmitting" class="button-loading">
          <svg class="yb-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3" />
            <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          {{ t('cart.guestLogin.saving') }}
        </span>
        <span v-else>{{ t('cart.guestLogin.save') }}</span>
      </button>
    </template>
  </MobileModal>
</template>

<style scoped lang="css">
.guest-form {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
}

.required {
  color: #ea3532;
}

.optional {
  font-weight: normal;
  color: var(--yb-color-darkgray);
}

.field-input {
  width: 100%;
  padding: var(--yb-spacing-sm);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  /* >= 16px prevents iOS Safari from zooming on focus */
  font-size: var(--yb-text-base);
  font-family: inherit;
  color: var(--yb-color-black);
  background-color: var(--yb-color-white);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--yb-color-primary);
  }

  &::placeholder {
    color: var(--yb-color-darkgray);
  }
}

.field-error {
  border-color: #ea3532;

  &:focus {
    border-color: #ea3532;
  }
}

.error-message {
  font-size: var(--yb-text-xs);
  color: #ea3532;
  margin: 0;
}

.submit-error {
  font-size: var(--yb-text-sm);
  color: #ea3532;
  text-align: center;
  padding: var(--yb-spacing-sm);
  background: #fef2f2;
  border-radius: var(--yb-radius-sm);
  margin: 0;
}

.section-separator {
  border: none;
  border-top: 1px solid var(--yb-color-lightgray);
  margin: 0;
}

.button-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--yb-spacing-xs);
}
</style>
